import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService }                    from '@ngx-translate/core';
import { UIRouter }                            from '@uirouter/core';
import { Subscription }                        from 'rxjs';

import { CodeTypeModel }                          from '../../core/models/code-type';
import { LinkComponent }                          from '../../table/cell-components/actions/link.component';
import { SessionService }                         from '../../sessions/session.service';
import { Table }                                  from '../../table/types/table';
import { TableQueryParameters }                   from '../../table/types/table-query-parameters';
import { TableService }                           from '../../table/table.service';
import { TableSwitchControlComponent }            from '../../table/cell-components/controls/switch-control/table-switch-control.component';
import { TransactionCenterFiltersModel }          from '../typings/transaction-center.filters';
import { TransactionCenterService }               from '../transaction-center.service';
import { TransactionCenterVendorInvoiceModel }    from './typings/transaction-center.vendor-invoice';
import { TransactionCenterVendorInvoicesService } from './transaction-center.vendor-invoices.service';

@Component({
  selector   : 'wor-transaction-center-vendor-invoices',
  template: require('./transaction-center.vendor-invoices.component.html')
})
export class TransactionCenterVendorInvoicesComponent implements OnDestroy, OnInit {
  @Input() invoices: Array<TransactionCenterVendorInvoiceModel> = [];
  @Input() statuses: Array<CodeTypeModel>                       = [];

  broadcastKey  = this.vendorInvoicesService.broadcastKey;
  tableCacheKey = this.vendorInvoicesService.tableCacheKey;

  filters : TransactionCenterFiltersModel = this.transactionCenterService.clearCachedWildcardDates(
    this.tableService.setFiltersFromCache(this.tableCacheKey, {
      endDate  : null,
      startDate: null,
      status   : 'I',
      value    : ''
    }),
    [ 'endDate', 'startDate' ]
  );

  searchSubscription: Subscription;
  tableSettings     : Table = {};

  constructor (
    private router                  : UIRouter,
    private sessionService          : SessionService,
    private tableService            : TableService,
    private transactionCenterService: TransactionCenterService,
    private translateService        : TranslateService,
    private vendorInvoicesService   : TransactionCenterVendorInvoicesService
  ) {}

  ngOnDestroy () : void {
    this.searchSubscription?.unsubscribe();
  }

  ngOnInit () : void {
    this.tableSettings = this.buildTableSettings();

    this.transactionCenterService.setSearch(this.filters.value || '');

    this.searchSubscription = this.transactionCenterService.searchChanges
      .subscribe(value => {
        if ( value !== null && value !== this.filters.value ) {
          this.filters.value = value;

          this.filterTable();
        }
      });
  }

  buildTableSettings () : Table {
    const currencySettings = this.sessionService.currency();

    return {
      broadcast: this.broadcastKey,
      cache    : {
        enabled: true,
        key    : this.tableCacheKey
      },
      columns: [
        {
          field : 'post_date',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.POST_DATE')
          },
          pipe: 'globalDate'
        },
        {
          field : 'vendor_invoice_number',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.REFERENCE')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                model: 'vendor_company_name'
              },
              outputs: {
                onClick: event => this.viewVendor(event)
              }
            },
            enabled: true,
            factory: LinkComponent
          },
          field : 'vendor_company_name',
          header: {
            text: this.translateService.instant('VENDOR_SPACE.SUPPLIER')
          },
          sortable: true
        },
        {
          field : 'invoice_type',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.INVOICE_TYPE')
          },
          pipe: 'invoiceType'
        },
        {
          field : 'vendor_invoice_payment_terms',
          header: {
            text: this.translateService.instant('GENERAL_SPACE.FIELD.PAYMENT_TERMS')
          }
        },
        {
          classes  : [ 'text-center' ],
          component: {
            bindings: {
              inputs: {
                disabled: true,
                model   : 'price_includes_tax'
              }
            },
            enabled: true,
            factory: TableSwitchControlComponent
          },
          field : 'price_includes_tax',
          header: {
            classes: 'text-center',
            text   : this.translateService.instant('GENERAL_SPACE.FIELD.PRICE_INCLUDES_TAX')
          },
          sortable: false
        },
        {
          classes: 'text-right',
          field  : 'gst',
          header : {
            classes: 'text-right',
            text   : currencySettings.tax_name
          },
          pipe: 'globalCurrency'
        },
        {
          classes: 'text-right',
          field  : 'total',
          header : {
            classes: 'text-right',
            text   : this.translateService.instant('GENERAL_SPACE.FIELD.TOTAL')
          },
          pipe: 'globalCurrency'
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_INVOICES_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : this.tableService.getCachedPaging(this.tableCacheKey).pageSize || 10,
        pageSizes : [ 10, 25, 50 ],
        queryCount: () => this.vendorInvoicesService.getCount(this.filters)
      },
      rows: {
        highlight: {
          enabled: true
        }
      },
      sorting: this.tableService.setSortingFromCache(this.tableCacheKey, {
        direction: 'desc',
        field    : 'post_date'
      })
    };
  }

  filterTable () : void {
    if ( window[ this.broadcastKey ] ) {
      window[ this.broadcastKey ](this.filters);
    }
  }

  getInvoices ( params : TableQueryParameters ) : Promise<Array<TransactionCenterVendorInvoiceModel>> {
    return this.vendorInvoicesService.get(this.filters, params)
      .then(( invoices : Array<TransactionCenterVendorInvoiceModel> ) => {
        this.invoices = invoices;

        return invoices;
      });
  }

  viewInvoice ( invoice : TransactionCenterVendorInvoiceModel ) : void {
    this.router.stateService.go('app.vendor-invoice', {
      id       : invoice.id,
      vendor_id: invoice.vendor_id
    });
  }

  viewVendor ( invoice : TransactionCenterVendorInvoiceModel ) : void {
    this.router.stateService.go('app.vendor', {
      id: invoice.vendor_id
    });
  }
}
